import React from 'react'
import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLocation } from 'react-router-dom'
import { Modal, Skeleton, ThemeProvider, Button, Typography, Tooltip, CircularProgress, Icon, createSvgIcon, ToggleButton } from '@mui/material'
import { useAuthInfo } from '@propelauth/react'
import { Box, IconButton } from '@mui/material'
import { Snackbar, Alert } from '@mui/material'
import FolderIcon from '@mui/icons-material/Folder'
import { v4 as uuidv4 } from 'uuid'

// CSS
import '../../App.css'

// Utils
import demoDocumentMRT from '../../utils/ExampleJSONs/ArtosGo/demoDocumentMRT.json'
import dsurDocumentMRT from '../../utils/ExampleJSONs/DSUR/DSURDocumentMRT.json'
import documentSpecificMRTExample from '../../utils/ExampleJSONs/documentSpecific/documentSpecificMRTExample.json'
import {
    checkDocFolder,
    getOneDocument,
    getMRTOutline,
    getDocumentFromId,
    generatePresignedUrl,
    getChunksFromIds,
    getSourceDataReferences,
    getMRTTables,
    getSectionNameFromSelectedText,
    updateDocumentStatus,
    uploadEditedFile,
    getDocumentTypeFromFilePath,
    getSourcesForSelectedText,
    getChunkFromId,
    getOneMRTTemplate
} from '../../utils/utils'
import { getDateFromObjectId } from '../../utils/dateUtils'
import { extractFileName, normalizeSectionTitle } from '../../utils/stringFormattingUtils'

// Components
import OnlyOfficeViewerComponent from './DocViewer'
import SourceSelectionModal from '../CreateDocumentPage/steps/SourceSelectionModal'

// Contexts
import { useSidebar } from '../../contexts/SidebarContext'
import { useUser } from '../../contexts/UserContext'
import { ArtosTheme } from '../../components/Themes'
import { useWebViewer } from '../../contexts/WebViewerContext'
import { useSectionTab } from '../../contexts/SectionTabContext'

// Tabs Rendering Components
import SourceDocumentChunkPreview from './tabs/SourceDocumentChunkPreview'
import SectionTabComponent from './tabs/SectionTabComponent'
import SidebarLayout from '../../components/SidebarLayout'

import InconsistencyPreview from './tabs/InconsistencyPreview'
import GeneratingTemplateModal from '../TemplatesPages/steps/modals/GeneratingTemplateModal'
import { BackIconButton } from '../../components/Buttons/NavigationButtons'
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GeneratingDocumentModal from '../CreateDocumentPage/steps/modals/GeneratingDocumentModal'

// Cache for getSectionsData results
const sectionsDataCache = new Map();

/**
 * Clears the cache for a specific document or all documents
 * @param {string} id - Document ID to clear from cache (optional)
 * @param {string} orgId - Organization ID (optional)
 */
const clearSectionsDataCache = (id = null, orgId = null) => {
    if (id && orgId) {
        const cacheKey = `${id}_${orgId}`;
        if (sectionsDataCache.has(cacheKey)) {
            sectionsDataCache.delete(cacheKey);
            console.log('[clearSectionsDataCache] Cache cleared for:', cacheKey);
        }
    } else {
        sectionsDataCache.clear();
        console.log('[clearSectionsDataCache] All cache cleared');
    }
};

/**
 * Returns the array of section data from the sampleDocumentMRT.
 * Encapsulated for easy future refactor to dynamic fetching.
 * Results are cached to ensure the function runs only once per document ID.
 */
const getSectionsData = async (
    id,
    authInfo,
    setLoading,
    setError,
    setSourceDocuments,
    setDateUploaded,
    logging=false,
    setDocumentMRTTables,
    setDocumentType
) => {
    // Create a unique cache key based on document ID and org
    const org = authInfo.orgHelper.getOrgs()[0].orgId;
    const cacheKey = `${id}_${org}`;

    // Check if we have cached data for this document
    if (sectionsDataCache.has(cacheKey)) {
        const cachedData = sectionsDataCache.get(cacheKey);
        console.log('[getSectionsData] Returning cached data for:', cacheKey);

        // Set the state values from cached data
        setSourceDocuments(cachedData.sourceDocuments);
        setDateUploaded(cachedData.dateUploaded);
        setDocumentType(cachedData.documentType);
        setDocumentMRTTables(cachedData.documentMRTTables);

        // Return the cached sections data
        return cachedData.sections;
    }

    console.log('[getSectionsData] No cache found, fetching data for:', cacheKey);
    const orgAccessToken = await authInfo.tokens.getAccessTokenForOrg(org);
    const orgs = authInfo.orgHelper.getOrgs()
    
    const mrtOutline = await getDocumentFromId(id, orgAccessToken)
    console.log('mrtOutline from getDocumentFromId', mrtOutline.document.id)
    const documentId = mrtOutline.document.id
    console.log('mrt_outline', mrtOutline.document)

    // Check if the document has a created_at property
    let date_uploaded = null
    if (mrtOutline.document.created_at) {
        date_uploaded = mrtOutline.document.created_at
    } else {
        date_uploaded = getDateFromObjectId(mrtOutline.document.id)
    }
    
    setDateUploaded(date_uploaded)

    console.log("Within getSectionsData")
    console.log('mrtOutline', mrtOutline)
    setDocumentType(mrtOutline.document.document_type)
    if (mrtOutline.document.document_type.includes('Tabulated Summary') || mrtOutline.document.mrt === undefined || mrtOutline.document.mrt.sections === undefined) {
        console.log('mrtOutline.document.document_type is a tabulated summary')
        return Promise.all([])
    }

    let sourceDocuments = []
    if (mrtOutline.document.all_sources && mrtOutline.document.all_sources.length > 0) {
        sourceDocuments = await Promise.all(mrtOutline.document.all_sources.map(async (source) => {
            console.log('document source', source)
            
            let document_source
            if (source.includes(org)) {
                // Org is in the source path
                document_source = source
            } else {
                // Add the org to the source path
                document_source = org + "/" + source
            }

            console.log('document_source', document_source)

            const document_name = source.split('/').pop()
            const presignedURLResponse = await generatePresignedUrl(document_source, orgAccessToken)
            const url = presignedURLResponse.url
            let date_uploaded = null
            if (mrtOutline.document.created_at) {
                date_uploaded = mrtOutline.document.created_at
            } else {
                date_uploaded = getDateFromObjectId(documentId)
            }
            const source_document_id = documentId + "-" + document_source

            console.log('source_document_id', source_document_id)

            const document_type_payload = {
                file_path: document_source
            }

            const document_type = await getDocumentTypeFromFilePath(document_type_payload, orgAccessToken)

            console.log('document_type', document_type)
            

            return {
                document_id: source_document_id,
                document_name: document_name,
                document_source_path: document_source,
                document_type: document_type.document_type,
                url: url,
                page: 1,
                date_uploaded: date_uploaded
            }
        }))
    }

    setSourceDocuments(sourceDocuments)
    
    const rawSections = Array.isArray(mrtOutline.document.mrt) ? mrtOutline.document.mrt : mrtOutline.document.mrt.sections
  
    // Collect all MRT table IDs from all sections first
    let rawAllMrtTableIdsArray = []
    
    rawSections.forEach(section => {
        section.rules.forEach(rule => {
            if (rule.rule_parameters && rule.rule_parameters.tfl_id) {
                rawAllMrtTableIdsArray.push({
                    section_number: section.additional_details.section_number,
                    tfl_id: rule.rule_parameters.tfl_id
                })
            }
        })
    })
    console.log('rawAllMrtTableIdsArray', rawAllMrtTableIdsArray)
    // Remove duplicates
    // const allMrtTableIdsArray = [...new Set(rawAllMrtTableIdsArray)]
    const allMrtTableIdsArray = rawAllMrtTableIdsArray.map(item => item.tfl_id)    
    console.log('allMrtTableIdsArray', allMrtTableIdsArray)
    
    // Fetch all MRT tables for the entire document
    let allMrtTables = []
    if (allMrtTableIdsArray.length > 0) {
        try {
            const fetchedMrtTables = await getMRTTables(setLoading, setError, allMrtTableIdsArray, orgAccessToken)
            allMrtTables = Array.isArray(fetchedMrtTables?.tables) ? fetchedMrtTables.tables : []
        } catch (error) {
            console.log('[getSectionsData] Error fetching MRT tables, using empty array:', error)
            allMrtTables = []
        }

        const allMrtTablesWithSectionNumbers = rawAllMrtTableIdsArray.map(item => {
            const foundTable = allMrtTables.find(table => table && (table.id === item.tfl_id || table._id === item.tfl_id))
            if (foundTable) {
                return {
                    uuid: uuidv4(),
                    section_number: item.section_number,
                    ...foundTable
                }
            }
            return null
        }).filter(item => item !== null)

        setDocumentMRTTables(allMrtTablesWithSectionNumbers)
    }

    const sectionPromises = rawSections.map(async (section) => {
        // Ids associated with ncr documents stored in the ncr_documents collection
        let sourceDataReferenceIdsArray = []
        // Ids associated with chunks stored in the mrt_example_chunks collection
        let chunkIdsArray = []
        // Ids associated with tables stored in the mrt_tables collection
        let mrtTableIdsArray = []

        const rawSectionSources = section.relevant_chunk_ids
            ? section.relevant_chunk_ids.map(chunk_id => chunk_id)
            : (section.relevant_chunks && Array.isArray(section.relevant_chunks))
                ? section.relevant_chunks.map(chunk => chunk.chunk_id)
                : []
        
        chunkIdsArray = rawSectionSources

        section.rules.forEach(rule => {

            // Check if the rule has a source data reference property AND that the property is not empty
            if (rule.source_data_references && rule.source_data_references.length > 0) {

                // Source data reference property's are arrays of objects that contain either a chunk_id or an identifier
                rule.source_data_references.forEach(sourceDataReference => {
                    // If the object has a chunk_id property
                    if (sourceDataReference.chunk_id) {
                        // add it to the chunkIdsArray
                        chunkIdsArray.push(sourceDataReference.chunk_id)
                    } else {
                        // If the object has an identifier property, add it to the sourceDataReferenceIdsArray
                        sourceDataReferenceIdsArray.push(sourceDataReference.identifier)
                    }
                })
            } 
            // Check if the rule has a rule_parameters object AND that the object has a tfl_id property
            else if (rule.rule_parameters && rule.rule_parameters.tfl_id) {

                // Add the table id to the mrtTableIdsArray
                mrtTableIdsArray.push(rule.rule_parameters.tfl_id)
            }
        })

        if (logging) {
            // console.log('mrtTableIdsArray', mrtTableIdsArray)
            // console.log('chunkIdsArray', chunkIdsArray)
            // console.log('sourceDataReferenceIdsArray', sourceDataReferenceIdsArray)
        }

        // Filter the relevant MRT tables for this section from the document-level collection
        const sectionMrtTables = allMrtTables.filter(table => 
            mrtTableIdsArray.includes(table.id || table._id || table.mrt_table_id)
        )

        // Chunk Objects whose id are the source_data_reference object's chunk_id
        let sectionSources = { chunks: [] }
        try {
            sectionSources = await getChunksFromIds(setLoading, setError, chunkIdsArray, orgAccessToken)
            if (!sectionSources || !sectionSources.chunks) {
                sectionSources = { chunks: [] }
            }
        } catch (error) {
            console.log('[getSectionsData] Error fetching section chunks, using empty array:', error)
            sectionSources = { chunks: [] }
        }

        // NCR Document Objects whose id are the source_data_reference object's identifier
        let sourceDataReferences = { documents: [] }
        try {
            sourceDataReferences = await getSourceDataReferences(setLoading, setError, sourceDataReferenceIdsArray, orgAccessToken)
            if (!sourceDataReferences || !sourceDataReferences.documents) {
                sourceDataReferences = { documents: [] }
            }
        } catch (error) {
            console.log('[getSectionsData] Error fetching source data references, using empty array:', error)
            sourceDataReferences = { documents: [] }
        }

        if (logging) {
            // console.log('mrtTables', mrtTables)
            // console.log('sourceDataReferences', sourceDataReferences)
            // console.log('sectionSources', sectionSources)
        }

        // Set AI Steps - await all promises
        const aiStepsPromises = Array.isArray(section.rules) ? section.rules.map(async (rule) => {
            const ruleParams = rule.rule_parameters || {};
            let documentName = null;
            let page = null;
            let sectionName = null;
            const getUrl = async () => {
                try {
                  console.log('[getUrl] Starting URL generation for rule type:', rule.rule_type);
                  console.log('[getUrl] Rule params:', ruleParams);
                  console.log('[getUrl] Full rule object:', rule);

                  let filePath = null;

                  // Handle different rule types
                  if (rule.rule_type === "secondary_tfl_reference") {
                    // Use getMRTTables for TFL references
                    console.log('[getUrl] Handling secondary_tfl_reference');
                    if (ruleParams.tfl_id) {
                      console.log('[getUrl] Fetching TFL table with ID:', ruleParams.tfl_id);
                      try {
                        const rawTableInfo = await getMRTTables(setLoading, setError, [ruleParams.tfl_id], orgAccessToken);
                        const tableInfo = rawTableInfo?.tables
                        console.log('[getUrl] Table info response:', tableInfo);

                        // Check if URL is directly provided in the response
                        if (tableInfo && tableInfo.length > 0) {
                          if (tableInfo[0].url) {
                            console.log('[getUrl] Found URL directly in table info:', tableInfo[0].url);
                            console.log('[getUrl] Table title:', tableInfo[0].table_title);
                            documentName = tableInfo[0].table_title;
                            return tableInfo[0].url;
                          } else if (tableInfo[0].file_path) {
                            filePath = tableInfo[0].file_path;
                          }
                        }
                      } catch (error) {
                        console.log('[getUrl] Table not found or error fetching table:', ruleParams.tfl_id, error);
                        return null;
                      }
                    } else {
                      console.log('[getUrl] No tfl_id found in ruleParams');
                    }
                  } else if (["literal_summarize_rule", "copy_paste_rule", "summarize_content", "fill_in_the_blank", "fill_in_blank", "copy_paste", "template_text"].includes(rule.rule_type)) {
                    // Use getChunkFromId for copy/summarize rules
                    console.log('[getUrl] Handling literal_summarize_rule or copy_paste_rule');
                    console.log('[getUrl] Rule Type:', rule.rule_type);
                    console.log('[getUrl] Rule params:', ruleParams);
                    const chunkId = ruleParams.source_chunk_id || rule.source_chunk_id || rule.rule_parameters?.source_chunk_id;
                    console.log('[getUrl] Chunk ID to fetch:', chunkId);
                    if (chunkId) {
                      console.log('[getUrl] Fetching chunk with ID:', chunkId);
                      try {
                        const rawChunkInfo = await getChunksFromIds(setLoading, setError, [chunkId], orgAccessToken);
                        const chunkInfo = rawChunkInfo?.chunks[0];
                        console.log('[getUrl] Chunk info response for ', rule.rule_type, ':', chunkInfo);

                        // Check if URL is directly provided in the response
                        if (chunkInfo) {
                          if (chunkInfo.url) {
                            console.log('[getUrl] Found URL directly in chunk info:', chunkInfo.url);
                            documentName = chunkInfo.document_name;
                            page = chunkInfo.page;
                            sectionName = chunkInfo.section_name || null;
                            return chunkInfo.url;
                          } else if (chunkInfo.file_path) {
                            filePath = chunkInfo.file_path;
                          }
                        }
                      } catch (error) {
                        console.log('[getUrl] Chunk not found or error fetching chunk:', chunkId, error);
                        return null;
                      }
                    } else {
                      console.log('[getUrl] No chunk ID found in ruleParams, rule, or rule.rule_parameters');
                    }
                  } else if (rule.rule_type === "tfl_table_and_summary") {
                    // Use getMRTTables for table and summary
                    console.log('[getUrl] Handling tfl_table_and_summary');
                    if (ruleParams.tfl_id) {
                      console.log('[getUrl] Fetching TFL table for table_and_summary with ID:', ruleParams.tfl_id);
                      try {
                        const rawTableInfo = await getMRTTables(setLoading, setError, [ruleParams.tfl_id], orgAccessToken);
                        const tableInfo = rawTableInfo?.tables
                        console.log('[getUrl] Table info for table_and_summary:', tableInfo);

                        // Check if URL is directly provided in the response
                        if (tableInfo && tableInfo.length > 0) {
                          if (tableInfo[0].url) {
                            console.log('[getUrl] Found URL directly in table info:', tableInfo[0].url);
                            documentName = tableInfo[0].table_title;
                            return tableInfo[0].url;
                          } else if (tableInfo[0].file_path) {
                            filePath = tableInfo[0].file_path;
                          }
                        }
                      } catch (error) {
                        console.log('[getUrl] Table not found or error fetching table:', ruleParams.tfl_id, error);
                        return null;
                      }
                    } else {
                      console.log('[getUrl] No tfl_id found in ruleParams for table_and_summary');
                    }
                  } else if (rule.rule_type === "ncr_intro_methods_conclusions") {
                    // Use getMRTTables for ncr intro methods conclusions
                    console.log('[getUrl] Handling ncr_intro_methods_conclusions');
                    console.log('[getUrl] NCR Intro Methods Conclusions Rule params:', ruleParams);
                    console.log('[getUrl] NCR Intro Methods Conclusions Target NCR ID:', ruleParams.target_ncr_id);
                    console.log('[getUrl] NCR Intro Methods Conclusions Section Name:', ruleParams.section_title);
                    try {
                        console.log('[getUrl] Fetching NCR with ID:', ruleParams.target_ncr_id);
                        const rawNcrInfo = await getSourceDataReferences(setLoading, setError, [ruleParams.target_ncr_id], orgAccessToken);
                        console.log('[getUrl] NCR info response:', rawNcrInfo);
                        const ncrInfo = rawNcrInfo?.documents[0];
                        console.log('[getUrl] NCR info response:', ncrInfo);

                        if (ncrInfo) {
                            if (ncrInfo.url) {
                                console.log('[getUrl] Found URL directly in NCR info:', ncrInfo.url);
                                documentName = ncrInfo.document_name;
                                return ncrInfo.url;
                            }
                            else if (ncrInfo.file_path) {
                                filePath = ncrInfo.file_path;
                            }
                        }
                    } catch (error) {
                        console.log('[getUrl] NCR not found or error fetching NCR:', ruleParams.target_ncr_id, error);
                        return null;
                    }
                  } else if (rule.rule_type === "template_text_rule" || rule.rule_type === "template_text") {
                    console.log('[getUrl] Handling template_text');
                    try {
                        console.log('[getUrl] Fetching template text with ID:', ruleParams.template_text_id);

                        // Validate template_id exists before making API call
                        const templateId = mrtOutline?.document?.template_id;
                        if (!templateId) {
                            console.warn('[getUrl] Missing template_id, cannot fetch template');
                            return null;
                        }

                        const rawTemplateInfo = await getOneMRTTemplate(templateId, orgAccessToken)
                        console.log('[getUrl] Template info response:', rawTemplateInfo);
                        const ttRuleParams = ruleParams
                        console.log('[getUrl] Template Text Rule params:', ttRuleParams);
                        documentName = rawTemplateInfo.template_name;
                        sectionName = ruleParams.content;
                        console.log('[getUrl] documentName:', documentName);
                        return rawTemplateInfo.url;

                    } catch (error) {
                        console.log('[getUrl] Template text not found or error fetching template text:', ruleParams.template_text_id, error);
                        return null;
                    }
                  } else {
                    console.log('[getUrl] Unhandled rule type:', rule.rule_type);
                  }

                  console.log('[getUrl] Extracted file_path:', filePath);

                  if (filePath) {
                    // Check if org_id is prepended, if not add it
                    const orgId = orgs[0].orgId;
                    const key = filePath.startsWith(orgId) ? filePath : `${orgId}/${filePath}`;
                    console.log('[getUrl] Final key for presigned URL:', key);

                    // Generate presigned URL
                    try {
                      const urlResponse = await generatePresignedUrl(key, orgAccessToken);
                      console.log('[getUrl] Presigned URL response:', urlResponse);
                      const finalUrl = urlResponse.url || urlResponse;
                      console.log('[getUrl] Returning final URL:', finalUrl);
                      return finalUrl;
                    } catch (error) {
                      console.log('[getUrl] Error generating presigned URL:', key, error);
                      return null;
                    }
                  }

                  console.log('[getUrl] No file path found, returning null');
                  return null;
                } catch (error) {
                  console.error('[getUrl] Error generating URL:', error);
                  return null;
                }
            };

            try {
                const url = await getUrl();
                // Only return the rule if it was successfully processed
                // If url is null but we still want to include the rule, return it
                // If we want to exclude rules without URLs, we would return null here
                return {
                    rule_type: rule.rule_type,
                    explanation: rule.explanation,
                    confidence_score: rule.confidence_score,
                    params: rule.rule_parameters,
                    generatedContent: rule.generated_content,
                    url: url,
                    documentName: documentName,
                    page: page,
                    sectionName: sectionName
                }
            } catch (error) {
                console.log('[aiSteps] Error processing rule, skipping:', rule, error);
                return null; // Return null for rules that fail to process
            }
        }) : []

        // Await all promises to resolve and filter out null values
        const resolvedAiSteps = await Promise.all(aiStepsPromises)
        const aiSteps = resolvedAiSteps.filter(step => step !== null)

        if (logging) {
            // console.log('aiSteps', aiSteps)
        }

        // Set Template Instructions
        const templateInstructionValue = section.template_instructions

        if (logging) {
            // console.log('templateInstructionValue', templateInstructionValue)
        }

        return {
            section_id: section.section_id,
            title: section.title,
            normalizedTitle: normalizeSectionTitle(section.title),
            templateInstructions: templateInstructionValue || null,
            sectionSources: sectionSources.chunks,
            sourceDataReferences: sourceDataReferences.documents,
            mrtTables: sectionMrtTables,
            aiSteps: aiSteps
        }
    });

    if (logging) {
        // console.log('sectionPromises', sectionPromises)
    }

    // Wait for all promises to resolve and return the actual values
    const sectionsResult = await Promise.all(sectionPromises);

    // Store the data in cache before returning
    const cacheData = {
        sections: sectionsResult,
        sourceDocuments: sourceDocuments,
        dateUploaded: date_uploaded,
        documentType: mrtOutline.document.document_type,
        documentMRTTables: rawAllMrtTableIdsArray.map(item => {
            const foundTable = allMrtTables.find(table => table && (table.id === item.tfl_id || table._id === item.tfl_id))
            if (foundTable) {
                return {
                    uuid: uuidv4(),
                    section_number: item.section_number,
                    ...foundTable
                }
            }
            return null
        }).filter(item => item !== null)
    };

    sectionsDataCache.set(cacheKey, cacheData);
    console.log('[getSectionsData] Data cached for:', cacheKey);

    return sectionsResult;
};

function UserAvatarBubble({
    avatar,
    idx,
    isContainerHovered,
    sx={}
}) {
    return (
        <Box
            sx={{
                ...sx,
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: avatar.backgroundColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: 15,
                color: avatar.color,
                border: `2px solid ${avatar.borderColor}`,
                boxShadow: `0 0 0 1px ${avatar.boxShadowColor}`,
                ml: idx === 0 ? 0 : (isContainerHovered ? 1 : -2),
                transition: 'all 0.2s ease',
            }}
        >
            {avatar.name}
        </Box>
    )
}

function DocumentHeaderWithAvatar({
    avatars,
}) {
    const [isHovered, setIsHovered] = useState(false);
    
    return (
        <Box
            sx={{
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            {/* Sidebar Header: Avatars + Last Updated */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 0,
                    background: '#fff',
                    borderRadius: 2,
                    minHeight: 56,
                    mb: 1,
                }}
            >
                {/* Avatars Row */}
                <Box 
                    sx={{ display: 'flex', alignItems: 'center' }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {/* Placeholder Avatars */}
                    {avatars.map((avatar, idx) => (
                        <UserAvatarBubble 
                            key={avatar.id}
                            avatar={avatar}
                            idx={idx}
                            isContainerHovered={isHovered}
                            sx={{
                                position: 'relative',
                                zIndex: avatars.length - idx,
                                '&:hover': {    
                                    zIndex: avatars.length + 1,
                                    transform: 'translateY(-2px)',
                                },
                            }}
                        />
                    ))}
                </Box>
                {/* Last updated text */}
                <Box sx={{ ml: 2, flex: 1 }}>
                    <span style={{ color: '#64748B', fontSize: 14, fontWeight: 500 }}>
                        Last updated Today, 5:12PM &middot;{' '}
                        <a
                            href="#"
                            style={{
                                color: '#1F477D',
                                textDecoration: 'underline',
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            See history
                        </a>
                    </span>
                </Box>
            </Box>
        </Box>
    )
}

const avatars = [
    {
        id: uuidv4(),
        name: 'AB',
        backgroundColor: '#E0E7EF',
        color: '#1F477D',
        borderColor: '#fff',
        boxShadowColor: '#E0E7EF'
    },
    {
        id: uuidv4(),
        name: 'CD',
        backgroundColor: '#B6C6E3',
        color: '#fff',
        borderColor: '#fff',
        boxShadowColor: '#B6C6E3'
    },
    {
        id: uuidv4(),
        name: 'EF',
        backgroundColor: '#F1B6B6',
        color: '#fff',
        borderColor: '#fff',
        boxShadowColor: '#F1B6B6'
    }
]

const MINIMUM_WORD_LENGTH_FOR_SECTION_SELECTION = 5

const SidebarOpenIcon = createSvgIcon(
    <svg
        xmlns="https://www.w3.org/2000/svg"
        viewBox="-2 -2 24 24"
        fill="currentColor"
        width="24px"
        height="24px"
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 0H17c3 0 3 3 3 3V17s0 3-3 3H3c-3 0-3-3-3-3V3S0 0 3 0M15 2V18h2c1 0 1-1 1-1V3s0-1-1-1H15M2 3V17s0 1 1 1H13V2H3C2 2 2 3 2 3" />
    </svg>
)

const SidebarClosedIcon = createSvgIcon(
    <svg
        xmlns="https://www.w3.org/2000/svg"
        viewBox="-2 -2 24 24"
        fill="currentColor"
        width="24px"
        height="24px"
    >
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 0H17c3 0 3 3 3 3V17s0 3-3 3H3c-3 0-3-3-3-3V3S0 0 3 0M2 3V17s0 1 1 1H13V2H3C2 2 2 3 2 3" />
    </svg>
)

function DocumentPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const { roles } = useUser()
    const { toggleSidebar } = useSidebar()
    const { setSectionInfo } = useSectionTab()
    const [documentSidebarOpen, setDocumentSidebarOpen] = useState(false)

    // Check if user is a Member (read-only access)
    const isMemberRole = roles.includes('Member')

    // User Auth
    const authInfo = useAuthInfo()
    const orgs = authInfo.orgHelper.getOrgs()

    const [mrtJson, setMrtJson] = useState(null)

    // Set initial tab based on role - Members skip Edit tab (0) and go to tab 1
    const [activeTab, setActiveTab] = useState(isMemberRole ? 1 : 0)
    
    const [webViewerLoading, setWebViewerLoading] = useState(true)
    const [editorInstance, setEditorInstance] = useState(null)
    
    const [isVectorizing, setIsVectorizing] = useState(false)
    
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [success, setSuccess] = useState(false)
    
    const [documentUrl, setDocumentUrl] = useState('')
    const [documentName, setDocumentName] = useState('')
    const [tflRunId, setTflRunId] = useState('')
    const [sourceIndex, setSourceIndex] = useState('')
    
    const [templateId, setTemplateId] = useState('')
    const [templateURL, setTemplateURL] = useState('')
    
    const [tableAssignments, setTableAssignments] = useState([])
    
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    
    const [selectedText, setSelectedText] = useState('')
    
    const [outputFolder, setOutputFolder] = useState('')
    const [documentSet, setDocumentSet] = useState('')

    const [selectedChunk, setSelectedChunk] = useState(null)
    const [templateTitle, setTemplateTitle] = useState('')
    
    // Persisted section dropdown state for SectionTabComponent
    const [sections, setSections] = useState([]); // <-- store fetched sections here
    const [selectedSectionIdx, setSelectedSectionIdx] = useState(-1)
    const [documentSources, setDocumentSources] = useState([]); // <-- store all section sources and their page numbers here
    const [sourceDataReferences, setSourceDataReferences] = useState([]);
    const [mrtTables, setMRTTables] = useState([]);
    const [documentStatus, setDocumentStatus] = useState('')
    const [documentStatusLoading, setDocumentStatusLoading] = useState(false)

    const [errorMessage, setErrorMessage] = useState('')
    
    const [regenText, setRegenText] = useState('')
    const [pastContent, setPastContent] = useState('')
    
    // UI State
    const [showSourceDocumentChunkPreview, setShowSourceDocumentChunkPreview] = useState(false)
    const [expanded, setExpanded] = useState("panel4");
    // NEW: State for inconsistency preview
    const [selectedInconsistencyPreview, setSelectedInconsistencyPreview] = useState(null)
    const [combinedDocumentSources, setCombinedDocumentSources] = useState([])
    const [isRegenerating, setIsRegenerating] = useState(false)
    const [sourceDocuments, setSourceDocuments] = useState([])
    const [selectedCloudFiles, setSelectedCloudFiles] = useState([])
    const [selectedCloudFileNames, setSelectedCloudFileNames] = useState([])
    const [multipleFileList, setMultipleFileList] = useState([])
    const [multipleFileNameList, setMultipleFileNameList] = useState([])
    const [sourceModalOpen, setSourceModalOpen] = useState(false)
    const [dateUploaded, setDateUploaded] = useState(null)
    const [templateNickname, setTemplateNickname] = useState('')
    const [documentMRTTables, setDocumentMRTTables] = useState([])
    const [connectorId, setConnectorId] = useState('')
    const [selectedSources, setSelectedSources] = useState([])
    const [selectedSectionSources, setSelectedSectionSources] = useState([])
    const [documentType, setDocumentType] = useState('')
    const [documentUpdatedAt, setDocumentUpdatedAt] = useState('')

    const fileInputRef = useRef(null)
    

    const isFullyLoaded = !loading && !webViewerLoading

    const demoIsFullyLoaded = true

    // documentId is the actual database document ID (from URL params)
    const documentId = id

    // documentKey is the OnlyOffice key that includes the timestamp for versioning
    // This ensures all users share the same key AND the key updates when document is saved
    // OnlyOffice only allows: 0-9, -, ., a-z, A-Z, _, =
    const sanitizeForOnlyOfficeKey = (str) => {
        if (!str) return str
        // Replace any characters not in the allowed set with underscores
        return str.replace(/[^0-9a-zA-Z._=-]/g, '_')
    }

    const documentKey = documentUpdatedAt
        ? `document-${id}-${sanitizeForOnlyOfficeKey(documentUpdatedAt.toString())}`
        : `document-${id}`

    const shortestAllowedTextLengthCheck = selectedText.split(' ').length > MINIMUM_WORD_LENGTH_FOR_SECTION_SELECTION
    const sectionTitleCheck = sections.some(section => {
        return (new RegExp(normalizeSectionTitle(selectedText))).test(section.normalizedTitle)
    })
    const fetchSectionNamePredicate = shortestAllowedTextLengthCheck && !sectionTitleCheck

    useEffect(() => {
        const fetchSectionName = async () => {
            console.log('fetchSectionName')
            console.log('selectedText', selectedText)

            const normalizedSelectedText = selectedText.replace(/[^a-zA-Z0-9]/g, '').toLowerCase()

            console.log('normalizedSelectedText', normalizedSelectedText)

            const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
            const result = await getSectionNameFromSelectedText(id, normalizedSelectedText, orgAccessToken)
            const sectionName = normalizeSectionTitle(result.section_name)

            console.log('sectionName', sectionName)

            const index = sections.findIndex(section => {
                console.log('section.normalizedTitle', section.normalizedTitle)
                console.log('sectionName', sectionName)
                console.log('section.normalizedTitle === sectionName', section.normalizedTitle === sectionName)

                return section.normalizedTitle === sectionName
            })
            
            console.log('index', index)

            setSelectedSectionIdx(index)
        }

        if (fetchSectionNamePredicate && !documentType.includes('Tabulated Summary')) {
            fetchSectionName()
        }
    }, [selectedText])

    useEffect(() => {
        console.log('selectedSectionIdx', selectedSectionIdx)
        const fetchSourcesForHighlightedText = async () => {
            console.log('selectedSectionIdx', selectedSectionIdx)
            const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
            
            // Get the current section directly from sections array using current selectedSectionIdx
            const currentSection = selectedSectionIdx !== -1 ? sections[selectedSectionIdx] : null
            const sectionId = currentSection ? currentSection.section_id : ""

            let payload = {
                document_id: id,
                section_id: sectionId,
                selected_text: selectedText
            }

            console.log('payload', payload)
            console.log('Using section:', currentSection?.title, 'with id:', sectionId)

            const result = await getSourcesForSelectedText(payload, orgAccessToken)

            console.log('result', result)
            setSelectedSources(result.sources)
        }

        if (selectedText && shortestAllowedTextLengthCheck && !documentType.includes('Tabulated Summary')) {
            fetchSourcesForHighlightedText()
        }
    }, [selectedSectionIdx, selectedText, sections])

    useEffect(() => {
        console.log('selectedSources', selectedSources)
    }, [selectedSources])

    // Fetch sections on mount or when id changes
    useEffect(() => {
        const fetchSections = async () => {
            const temp_id = "68649c4255bd6b9500db4c01"
            const usable_id = id || temp_id
            const data = await getSectionsData(
                usable_id,
                authInfo,
                setLoading,
                setError,
                setSourceDocuments,
                setDateUploaded,
                false,
                setDocumentMRTTables,
                setDocumentType
            );
            console.log('data', data)
            setSections(data);
            console.log('sections', data)
            // Auto-select first section once loaded
            if (data && data.length > 0) {
                setSelectedSectionIdx(0);
            }
        };
        fetchSections();

        // Optional: Clear cache when component unmounts or ID changes
        // This helps prevent memory leaks for long-running sessions
        return () => {
            // Uncomment if you want to clear cache on unmount
            // clearSectionsDataCache(id, authInfo.orgHelper.getOrgs()[0]?.orgId)
        };
    }, [])
    
    useEffect(() => {
        console.log('editorInstance', editorInstance)
        console.log('mrtJson', mrtJson)
        console.log('mrtJson === undefined', mrtJson === undefined)
        console.log('mrtJson === {}', JSON.stringify(mrtJson) === '{}')
    }, [editorInstance, mrtJson])
    
    // Single useEffect for text selection
    useEffect(() => {
        let cursorConnector = null
        const handleTextSelection = (selection) => {
            if (cursorConnector) {
                cursorConnector.executeMethod(
                    'GetSelectedText',
                    [{
                        'Numbering': false,
                        'Math': false,
                        'NewLineSeparator': '\n'
                    }],
                    function (text) {
                        if (text && text !== selectedText) {
                            const trimmedText = text.trim().replace(/[\r\n|\n|\r|\s]+$/g, ' ')
                            setSelectedText(trimmedText)
                        }
                    }
                )
            }
        }
        
        // setTimeout(() => {
        if (editorInstance) {
            cursorConnector = editorInstance.createConnector()
            
            cursorConnector.attachEvent(
                'onTargetPositionChanged',
                handleTextSelection
            )
        }
        // }, 10000) // Delay to allow document editor to load - may need to adjust this method
        
        return () => {
            try {
                if (
                    cursorConnector &&
                    editorInstance
                ) {
                    cursorConnector.detachEvent(
                        'onTargetPositionChanged',
                        handleTextSelection
                    )
                    cursorConnector = null
                }
            } catch (error) {
                console.warn('Error cleaning up document editor events:', error)
            }
        }
    }, [documentUrl, editorInstance]) // Add documentUrl as a dependency to re-run the effect when the document changes

    let sectionSelectionConnector = null

    const handleOptionSelect = (selected) => {
        console.log('handleOptionSelect')
        console.log('selected', selected)

        const sectionName = selected.title.replace(/^[\d.]+\s*/, '')

        const tableOfContentsSearchProperties = {
            oProperties: {
                searchString: "Table of Contents", // The text to search for
                matchCase: false, // Case sensitive search
                isForward: true, // Search direction
            },
            searchString: "Table of Contents", // The text to search for
            matchCase: false, // Case sensitive search
            isForward: true, // Search direction
        }

        const searchProperties = {
            oProperties: {
                searchString: sectionName, // The text to search for
                matchCase: false, // Case sensitive search
                isForward: true, // Search direction
            },
            searchString: sectionName, // The text to search for
            matchCase: false, // Case sensitive search
            isForward: true, // Search direction
        }
        
        sectionSelectionConnector = editorInstance.createConnector()
        sectionSelectionConnector.executeMethod(
            'SearchNext',
            [tableOfContentsSearchProperties],
            function(match) {
                console.log('tableOfContentsSearchProperties')
                sectionSelectionConnector.executeMethod(
                    'GetSelectedText',
                    [{"Numbering": false, "Math": false}],
                    function(sentence) {
                        console.log('sentence', sentence)
                        sectionSelectionConnector.executeMethod(
                            'SearchNext',
                            [searchProperties],
                            function(match) {
                                sectionSelectionConnector.executeMethod(
                                    'SearchNext',
                                    [searchProperties]
                                )
                            }
                        )
                    }
                )
            }
        )
    }
    
    useEffect(() => {
        async function retrieveData() {
            const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
            try {
                setLoading(true)
                
                const result = await getOneDocument(
                    setLoading,
                    setError,
                    id,
                    orgAccessToken
                )

                console.log("result", result)
                
                const temp_id = "68649c4255bd6b9500db4c01"
                const usable_id = id || temp_id
                
                let document = await getDocumentFromId(
                    usable_id,
                    orgAccessToken
                )

                console.log("document in retrieveData", document)

                setDocumentStatus(document.document.status)

                // Extract updated_at timestamp for document key versioning
                const updatedAt = document.document.updated_at || document.document.created_at || Date.now()
                setDocumentUpdatedAt(updatedAt)

                const specificMRT = document.document.mrt
                
                console.log('specificMRT', specificMRT)
                console.log('specificMRT.sections', specificMRT.sections)
                
                setMrtJson(specificMRT)
                
                if (specificMRT) {
                    setTflRunId(specificMRT.tfl_run_id || '')
                    setSourceIndex(specificMRT.source_index || '')
                    setDocumentSet(specificMRT.document_set || specificMRT.document_set_name || '')
                } else {
                    setTflRunId('')
                    setSourceIndex('')
                }

                let template_url = result['template_url']
                
                if (!template_url) {
                    if ((result["template_id"] || "") !== "") {
                        let key = orgs[0].orgId + "/" + (result['template_id'])
                        console.log("key: ", key)
                        template_url = await generatePresignedUrl(key, orgAccessToken)
                    }
                }

                console.log("template_url: ", template_url)
                
                setDocumentUrl(result['url'])
                setDocumentName(result['document_name'])
                setDocumentSet(result['document_set'])
                setTemplateId(result['template_id'] || "")
                setTemplateNickname(document.document.template_nickname || "")
                setTemplateURL(template_url)
                setTemplateTitle(result['template_nickname'] || result['template_name'])

                // This works as long as we are only using a single connector data id for a document
                // TODO: Replace this when that is no longer the case
                if (document.document.mrt === undefined || document.document.mrt.sections === undefined) {
                    setConnectorId("")
                } else if (document.document.document_type.includes('Tabulated Summary')) {
                    setConnectorId(document.document.template_id)
                } else {
                    console.log('typeof specificMRT?.connector_data_ids', typeof specificMRT?.connector_data_ids)
                    let connectorIdField = ""
                    if (typeof specificMRT?.connector_data_ids === 'undefined') {
                        connectorIdField = "connector_data_id"
                    } else {
                        connectorIdField = "connector_data_ids"
                    }
                    if (typeof specificMRT?.[connectorIdField] === 'string') {
                        console.log('specificMRT?.connector_data_ids is a string', specificMRT?.[connectorIdField])
                        setConnectorId(specificMRT?.[connectorIdField] || "")
                    } else if (Array.isArray(specificMRT?.[connectorIdField]) && specificMRT?.[connectorIdField].length > 0) {
                        console.log('specificMRT?.connector_data_ids is an array', specificMRT?.[connectorIdField])
                        setConnectorId(specificMRT?.[connectorIdField][0] || "")
                    } else {
                        console.log('specificMRT?.connector_data_ids is null or empty', specificMRT?.[connectorIdField])
                        setConnectorId("")
                    }
                }

                setLoading(false)
                setError(false)
            } catch (error) {
                console.error(error)
                try {
                    // Try to check document status if available
                    if (document?.document?.status === 'Generating') {
                        setErrorMessage('Document is still generating. Please try again later.')
                    } else {
                        setErrorMessage('Error loading document')
                    }
                } catch (innerError) {
                    // If we can't access document status, show generic error
                    setErrorMessage('Error loading document')
                }
                setError(true)
            } finally {
                setLoading(false)
            }
        }
        //
        // TODO: If changing the position of this call breaks the document loading,
        // then we need to move it back to the bottom of the useEffect
        //
        if (!mrtJson) {
            retrieveData()
        }
        
        const getOutlineContent = async () => {
            console.log('Within getOutlineContent')
            console.log(mrtJson)
            try {
                // Extract tfl_run_id and source_index from mrtJson
                const tflRunId = mrtJson?.tfl_run_id
                const sourceIndex = mrtJson?.source_index
                
                const temp_id = "68649c4255bd6b9500db4c01"
                const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
                
                const usable_id = id || temp_id
                
                const rawJSON = await getDocumentFromId(
                    usable_id,
                    orgAccessToken
                )
                
                console.log('rawJSON', rawJSON)
                
                const rawSections = Array.isArray(rawJSON.document.mrt) ? rawJSON.document.mrt : rawJSON.document.mrt.sections
                
                console.log('rawSections', rawSections)

                if (rawSections === undefined) {
                    setSectionInfo([])
                    setError(false)
                    setLoading(false)
                    return
                }

                const jsonWithUUID = rawSections.map((section) => {
                    if (section.rules) {
                        const rulesWithUUID = section.rules.map((rule) => {
                            if (rule.source_data_references && rule.source_data_references.length > 0) {
                                const sourceDataReferencesWithUUID = rule.source_data_references.map((sourceDataReference) => {
                                    return {
                                        id: uuidv4(),
                                        ...sourceDataReference,
                                    }
                                })
                                return {
                                    id: uuidv4(),
                                    ...rule,
                                    source_data_references: sourceDataReferencesWithUUID,
                                }
                            }
                            else {
                                return {
                                    id: uuidv4(),
                                    ...rule,
                                }
                            }
                        })
                        return {
                            id: uuidv4(),
                            normalizedTitle: normalizeSectionTitle(section.title),
                            ...section,
                            rules: rulesWithUUID,
                        }
                    } else {
                        return {
                            id: uuidv4(),
                            normalizedTitle: normalizeSectionTitle(section.title),
                            ...section,
                        }
                    }
                })
                
                console.log('jsonWithUUID', jsonWithUUID)
                
                setSectionInfo(jsonWithUUID)
                
                setError(false)
            } catch (error) {
                console.error('Error getting outline content:', error)
                
                setErrorMessage('Document is still generating. Please try again later.')
                
                setError(true)
            } finally {
                setLoading(false)
            }
        }
        
        if (mrtJson && !documentType.includes('Tabulated Summary')) {
            getOutlineContent()
        }
        
        // TODO: If changing the position of this call breaks the document loading,
        // then we need to move it back here
        //
        // retrieveData()
    }, [mrtJson])
    
    useEffect(() => {
        async function checkFolder() {
            const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
            const result = await checkDocFolder(orgAccessToken, id)
            var folder = result['folder']
            setOutputFolder(folder)
        }
        checkFolder()
    }, [])

    // useEffect(() => {
        //     var roles = user.roles.map((role) => role.toLowerCase())
        //     if (documentSet !== '') {
            //         if (outputFolder === 'internal') {
                //             if (!roles.includes('internal')) {
                    //                 console.log(
                        //                     'redirecting to document set -- user not internal but folder is internal'
                        //                 )
    //                 window.location.href = `/documentSet/${documentSet}?generating=true`
    //             } else if (roles.includes('internal')) {
    //             }
    //         }
    //     }
    // }, [outputFolder, documentSet])

    async function getOrgAccessToken(orgId) {
        return await authInfo.tokens.getAccessTokenForOrg(orgId)
    }

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue)
    }

    const handleTableAssignmentsUpdate = (newAssignments) => {
        setTableAssignments(newAssignments)
    }

    const handleWebViewerLoaded = () => {
        setWebViewerLoading(false)
    }

    // Accordion expand/collapse handler
    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleFileSelect = () => {
        setSourceModalOpen(true)
    }

    const handleSourceSelect = (selectedFiles) => {
        // console.log('handleSourceSelect')
        // console.log('selectedFiles', selectedFiles)
        // Store full paths in selectedCloudFiles
        setSelectedCloudFiles(
            selectedFiles.map((file) => ({
                name: file.name,
                url: file.url,
                id: file.id,
                isCloud: true,
            }))
        )

        // Store clean filenames using the same cleaning method as handleGenerateDocument
        const cleanFileNames = selectedFiles.map((file) => {
            const index = file.name.indexOf('/')
            return index !== -1 ? file.name.substring(index + 1) : file.name
        })
        setSelectedCloudFileNames(cleanFileNames)

        // Update the file name list to match exactly what's selected
        setMultipleFileNameList((prevList) => {
            // Remove all cloud files first
            const localFiles = prevList.filter((name) =>
                multipleFileList.some((file) => file.name === name)
            )
            // Add all currently selected cloud files
            return [...localFiles, ...cleanFileNames]
        })
    }

    // const handleTableInserted = async (tableTitle, url, message) => {
    //     setSnackbarMessage(message || 'Table successfully inserted')
    //     setSnackbarOpen(true)

    //     if (webViewerInstance) {
    //         const doc = webViewerInstance.Core.documentViewer
    //         await delay(1500)
    //         await doc
    //             .loadDocument(url, {
    //                 extension: 'docx',
    //                 enableOfficeEditing: true,
    //             })
    //             .then(async () => {
    //                 await delay(1000)
    //                 webViewerInstance.UI.searchTextFull(tableTitle, {
    //                     caseSensitive: true,
    //                     wholeWord: true,
    //                     wildcard: false,
    //                     regex: false,
    //                     searchUp: true,
    //                     ambientString: false,
    //                 })
    //             })
    //     }
    // }

    // File upload handler
    const handleFileSelectInternal = async (event) => {
        const file = event.target.files[0]
        if (!file) return
        
        try {
            setLoading(true)
            
            // Read file content
            const reader = new FileReader()
            reader.onload = async (e) => {
                const fileContent = e.target.result
                
                // Get org access token
                const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
                
                // Call uploadEditedFile
                const result = await uploadEditedFile(
                    setLoading,
                    [file],
                    [fileContent],
                    setError,
                    id,
                    orgAccessToken
                )

                if (result) {
                    // Clear the cache for this document since it was updated
                    clearSectionsDataCache(id, orgs[0].orgId)

                    // Force hard refresh immediately after successful upload (bypass cache)
                    // Using timestamp to ensure browser doesn't use cached version
                    const timestamp = new Date().getTime()
                    const currentUrl = window.location.href.split('?')[0]
                    window.location.href = `${currentUrl}?t=${timestamp}`
                }
            }
            
            reader.onerror = () => {
                setError(true)
                setErrorMessage('Failed to read file')
            }
            
            reader.readAsArrayBuffer(file)
            
        } catch (error) {
            console.error('Error uploading file:', error)
            setError(true)
            setErrorMessage('Failed to upload file')
        } finally {
            // Reset the input value so the same file can be selected again
            if (fileInputRef.current) {
                fileInputRef.current.value = ''
            }
        }
    }

    const handleRegenerateDocumentModalClose = async () => {
        // Reset states and navigate immediately
        setLoading(false)
        setSuccess(false)
        setError(false)
        setIsRegenerating(false)
    }

    const SidebarSkeletonLoader = () => (
        <Box sx={{ padding: 2 }}>
            <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                sx={{ mb: 2 }}
            />
            <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1 }} />
            <Skeleton
                variant="rectangular"
                width="100%"
                height={100}
                sx={{ mb: 2 }}
            />
            <Skeleton variant="text" width="85%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="75%" height={20} sx={{ mb: 1 }} />
        </Box>
    )

    useEffect(() => {
        console.log('isVectorizing', isVectorizing)
    }, [isVectorizing])

    const handleSnackbarClose = (reason) => {
        if (reason === 'clickaway') {
            return
        }
        setSnackbarOpen(false)
    }

    useEffect(() => {
        // Collapse sidebar when component mounts
        toggleSidebar()
    }, [])

    const handleOpenTemplateButtonClick = () => {
        console.log('handleOpenTemplateButtonClick')
        console.log('templateURL', templateURL)
        if (templateURL.url) {
            setSelectedInconsistencyPreview({
                fileUrl: templateURL.url,
                page: 1,
                title: templateTitle
            })
        } else {
            setSelectedInconsistencyPreview({
                fileUrl: templateURL,
                page: 1,
                title: templateTitle
            })
        }
    }

    return (
        <Box className="full-width-container" style={{ padding: 0, overflow: 'hidden' }}>
            <Box className="inner-container" style={{ 
                margin: 0, 
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
            }}>
                {/* Sub Header Container */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderBottom: '1px solid #E5E7EB',
                    backgroundColor: '#fff',
                    zIndex: 1100,
                    flex: '0 0 auto', // Don't grow or shrink, take natural height
                }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: '8px',
                        }}
                    >
                        <BackIconButton 
                            onClick={() => {
                                navigate(`/documentSet/${documentSet}`)
                            }}
                            sx={{
                                height: '16px',
                                width: '16px',
                                margin: 0,
                                color: '#374151'
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#374151',
                                margin: 0,
                            }}
                        >
                            {documentName.split("/").pop()} · Template: {templateNickname} ·
                        </Typography>
                        <IconButton
                            disableTouchRipple={true}
                            disabled={loading}
                            onClick={() => {
                                navigate(`/documentSet/${documentSet}`)
                            }}
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#374151',
                                fontSize: '14px',
                                fontWeight: '600',
                                textTransform: 'none',
                                padding: '0px',
                                margin: '-4px',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                    color: '#000000',
                                },
                            }}
                        >
                            <FolderIcon />
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#374151',
                                margin: 0,
                                '&:hover': {
                                    color: '#000000',
                                },
                            }}>
                                {documentSet}
                            </Typography>
                        </IconButton>
                        { roles.includes('Internal') && false &&(
                            <>
                                <Tooltip title="Click to update document status">
                                    <span>
                                        <Button
                                            variant="contained"
                                            color={
                                                documentStatus === 'Generating' ? 'warning' :
                                                documentStatus === 'Generated' ? 'success' :
                                                documentStatus === 'Completed' ? 'success' :
                                                documentStatus === 'Failed' ? 'error' :
                                                documentStatus === 'Error' ? 'error' :
                                                'primary'
                                            }
                                            disabled={documentStatusLoading || loading}
                                            onClick={async () => {
                                        try {
                                            setDocumentStatusLoading(true)
                                            const orgAccessToken = await getOrgAccessToken(orgs[0].orgId)
                                            
                                            // Update the document status
                                            const result = await updateDocumentStatus(id, orgAccessToken)
                                            // console.log('updateDocumentStatus result', result)
                                            
                                            // Refetch the document to get the latest status from DB
                                            const updatedDocument = await getDocumentFromId(id, orgAccessToken)
                                            // console.log('updatedDocument', updatedDocument)
                                            
                                            if (updatedDocument && updatedDocument.document && updatedDocument.document.status) {
                                                const newStatus = updatedDocument.document.status
                                                setDocumentStatus(newStatus)
                                                
                                                setSnackbarMessage(`Document status updated to: ${newStatus}`)
                                                setSnackbarOpen(true)
                                                
                                                // Wait a moment for the snackbar to show, then refresh
                                                setTimeout(() => {
                                                    window.location.reload()
                                                }, 1500)
                                            } else {
                                                throw new Error('Could not retrieve updated status')
                                            }
                                        } catch (error) {
                                            console.error('Error updating document status:', error)
                                            setSnackbarMessage('Failed to update document status')
                                            setSnackbarOpen(true)
                                            
                                            // Still refresh the page even on error to reset state
                                            setTimeout(() => {
                                                window.location.reload()
                                            }, 1500)
                                        } finally {
                                            setDocumentStatusLoading(false)
                                        }
                                    }}
                                    sx={{
                                        minWidth: 120,
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    {documentStatusLoading ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CircularProgress size={16} color="inherit" />
                                            <Typography>Updating...</Typography>
                                        </Box>
                                    ) : (
                                        <Typography>Status: {documentStatus || 'Unknown'}</Typography>
                                    )}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                    {/* Debug Buttons - Development Only */}
                    {process.env.NODE_ENV === 'development' && false && (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                                size="small"
                                onClick={() => {
                                    console.log('=== DOCUMENT PAGE LOCAL STORAGE DEBUG ===')
                                    console.log(`Document ID: ${id}`)
                                    console.log(`Document Name: ${documentName}`)
                                    console.log(`Template: ${templateNickname}`)
                                    console.log(`Document Set: ${documentSet}`)
                                    console.log('=====================================\n')
                                    
                                    const allKeys = []
                                    const documentRelatedKeys = []
                                    
                                    // Get all localStorage keys
                                    for (let i = 0; i < localStorage.length; i++) {
                                        const key = localStorage.key(i)
                                        allKeys.push(key)
                                        
                                        // Check if key is related to this document
                                        if (key && (key.includes(id) || 
                                            key.includes(documentName) || 
                                            key.includes(templateNickname))) {
                                            documentRelatedKeys.push(key)
                                        }
                                    }
                                    
                                    console.log('=== ALL LOCAL STORAGE KEYS ===')
                                    console.log(`Total keys: ${allKeys.length}`)
                                    
                                    // Log all keys with their values
                                    allKeys.forEach(key => {
                                        const value = localStorage.getItem(key)
                                        try {
                                            const parsed = JSON.parse(value)
                                            console.log(`📦 ${key}:`, parsed)
                                        } catch {
                                            console.log(`📦 ${key}:`, value)
                                        }
                                    })
                                    
                                    console.log('\n=== DOCUMENT-SPECIFIC KEYS ===')
                                    console.log(`Document-related keys: ${documentRelatedKeys.length}`)
                                    documentRelatedKeys.forEach(key => {
                                        console.log(`  - ${key}`)
                                    })
                                    
                                    console.log('\n=== CURRENT PAGE STATE ===')
                                    console.log('Sections:', sections?.length || 0)
                                    console.log('Source Documents:', sourceDocuments?.length || 0)
                                    console.log('MRT Tables:', documentMRTTables?.length || 0)
                                    console.log('Active Tab:', activeTab)
                                    console.log('Is Regenerating:', isRegenerating)
                                    console.log('=====================================\n')
                                }}
                                sx={{ 
                                    textTransform: 'none',
                                    p: '0px',
                                    borderRadius: '6px',
                                    boxShadow: 'inset 0px 0px 0px 1px #10B981',
                                    backgroundColor: '#ECFDF5',
                                    '&:hover': {
                                        backgroundColor: '#D1FAE5',
                                    },
                                    minWidth: 'auto'
                                }}
                            >
                                <Typography sx={{ 
                                    lineHeight: '16px', 
                                    fontSize: '12px', 
                                    fontWeight: 'medium', 
                                    color: '#047857', 
                                    py: '6px', 
                                    px: '10px' 
                                }}>
                                    🔍 Log All Storage
                                </Typography>
                            </Button>
                            <Button
                                size="small"
                                onClick={() => {
                                    if (window.confirm('⚠️ WARNING: This will clear ALL localStorage data and refresh the page.\n\nThis action cannot be undone. Are you sure?')) {
                                        console.log('🗑️ Clearing all localStorage...')
                                        
                                        // Get count before clearing
                                        const totalKeys = localStorage.length
                                        
                                        // Log all keys that will be removed
                                        const removedKeys = []
                                        for (let i = 0; i < localStorage.length; i++) {
                                            removedKeys.push(localStorage.key(i))
                                        }
                                        
                                        // Clear everything
                                        localStorage.clear()
                                        
                                        console.log(`✅ Cleared ${totalKeys} localStorage entries`)
                                        console.log('Removed keys:', removedKeys)
                                        
                                        // Also clear sessionStorage for good measure
                                        sessionStorage.clear()
                                        console.log('✅ Also cleared sessionStorage')
                                        
                                        // Hard refresh the page
                                        console.log('🔄 Refreshing page...')
                                        window.location.reload(true)
                                    }
                                }}
                                sx={{ 
                                    textTransform: 'none',
                                    p: '0px',
                                    borderRadius: '6px',
                                    boxShadow: 'inset 0px 0px 0px 1px #EF4444',
                                    backgroundColor: '#FEE2E2',
                                    '&:hover': {
                                        backgroundColor: '#FECACA',
                                    },
                                    minWidth: 'auto'
                                }}
                            >
                                <Typography sx={{ 
                                    lineHeight: '16px', 
                                    fontSize: '12px', 
                                    fontWeight: 'medium', 
                                    color: '#B91C1C', 
                                    py: '6px', 
                                    px: '10px' 
                                }}>
                                    💣 Clear All & Refresh
                                </Typography>
                            </Button>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Tooltip title="Upload an edited file">
                            <IconButton
                                disableTouchRipple={true}
                                sx={{
                                    width: '24px',
                                    height: '24px',
                                    margin: 0,
                                    color: '#374151',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        color: '#000000',
                                    }
                                }}
                                onClick={() => {
                                    fileInputRef.current?.click()
                                    setTimeout(() => {
                                        window.location.reload()
                                    }, 10000)
                                }}
                                disabled={loading}
                            >
                                <UploadFileIcon />
                            </IconButton>
                        </Tooltip>
                        {JSON.stringify(mrtJson) !== '{}' && (
                            <Tooltip title="Toggle document sidebar">
                                <IconButton
                                    disableTouchRipple={true}
                                    onClick={() => {
                                        setDocumentSidebarOpen(!documentSidebarOpen)
                                    }}
                                    sx={{
                                        width: '24px',
                                        height: '24px',
                                        margin: 0,
                                        color: '#374151',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            color: '#000000',
                                        }
                                    }}
                                    disabled={loading}
                                >
                                    {documentSidebarOpen ? (
                                        <SidebarOpenIcon />
                                    ) : (
                                        <SidebarClosedIcon />
                                    )}
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
                {/* Main Content Container */}
                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        overflowX: 'none',
                        flex: 1, // Take remaining height
                        minHeight: 0, // Important for proper flex child behavior
                        overflow: 'hidden'
                    }}
                >
                    {/* Webviewer Container */}
                    <Box
                        className="webviewContainer"
                        style={{
                            flex:
                                activeTab === 4 ||
                                showSourceDocumentChunkPreview
                                    ? 1
                                    : selectedInconsistencyPreview
                                        ? 1
                                        : 2,
                            width:
                                activeTab === 4 ||
                                showSourceDocumentChunkPreview
                                    ? '50%'
                                    : selectedInconsistencyPreview
                                        ? '50%'
                                        : documentType.includes('Tabulated Summary') 
                                            ? '100%' 
                                            : documentSidebarOpen
                                                ? '100%'
                                                : '66%',
                            visibility: 'visible',
                            transition: 'all 0.3s ease',
                            height: '100%', // Ensure it fills the parent height
                            overflow: 'hidden',
                            // transition: 'all 0.3s ease',
                        }}
                    >
                        <OnlyOfficeViewerComponent
                            documentId={documentId}
                            initialFile={documentUrl}
                            documentName={documentName}
                            onLoaded={handleWebViewerLoaded}
                            documentKey={documentKey}
                            displayType='desktop'
                            editorInstance={editorInstance}
                            setEditorInstance={setEditorInstance}
                            readOnly={isMemberRole}
                        />
                    </Box>
                    {/* Right Panel Container */}
                    <Box
                        className="right-panel"
                        style={{
                            flex: selectedInconsistencyPreview ? 1 : 1,
                            width: showSourceDocumentChunkPreview
                                ? '25vw'
                                : selectedInconsistencyPreview
                                    ? '45vw'
                                    : (documentType.includes('Tabulated Summary') || JSON.stringify(mrtJson) === '{}')
                                        ? '0vw' 
                                        : documentSidebarOpen
                                            ? '0vw'
                                            : '25vw',
                            maxWidth: showSourceDocumentChunkPreview
                                ? '30vw'
                                : selectedInconsistencyPreview
                                    ? '45vw'
                                    : (documentType.includes('Tabulated Summary') || JSON.stringify(mrtJson) === '{}')
                                        ? '0vw' 
                                        : documentSidebarOpen
                                            ? '0vw'
                                            : '30vw',
                            display: 'flex',
                            flexDirection: 'row',
                            height: '100%',
                            overflow: 'auto', // Allow scrolling within the right panel if needed
                            transition: 'all 0.3s ease-in-out 0.1s',
                        }}
                    >
                        {/* If preview is open, show only the preview in the sidebar */}
                        {documentType.includes('Tabulated Summary') ? (
                            <Box>
                            </Box>
                        ): selectedInconsistencyPreview ? (
                            <Box style={{
                                flex: 1,
                                minWidth: 0,
                                height: '100%',
                                background: '#F8FAFC',
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                maxWidth: '100%',
                            }}>
                                <InconsistencyPreview
                                    fileUrl={selectedInconsistencyPreview.fileUrl}
                                    page={selectedInconsistencyPreview.page}
                                    onClose={() => setSelectedInconsistencyPreview(null)}
                                    title={selectedInconsistencyPreview.title}
                                    selectedText={selectedInconsistencyPreview.selectedText}
                                />
                            </Box>
                        ) : (
                            // Main sidebar content (default)
                            <Box style={{
                                flex: 1,
                                minWidth: 0,
                                height: '100%',
                                background: '#fff',
                                display: 'flex',
                                flexDirection: 'column',
                            }}>
                                {/* {!isFullyLoaded || isVectorizing ? ( */}
                                {!demoIsFullyLoaded || isVectorizing ? (
                                    <>
                                        <Box
                                            sx={{
                                                borderBottom: 1,
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <ThemeProvider theme={ArtosTheme}>
                                                <SidebarSkeletonLoader />
                                            </ThemeProvider>
                                        </Box>
                                    </>
                                ) : showSourceDocumentChunkPreview && selectedChunk ? (
                                    <SourceDocumentChunkPreview
                                        chunk_info={selectedChunk}
                                        title={extractFileName(selectedChunk.url)}
                                        onClose={() => {
                                            setShowSourceDocumentChunkPreview(false)
                                            setSelectedChunk(null)
                                        }}
                                    />
                                ) : (
                                    <>
                                        {/* <Box sx={{ width: '100%' }}>
                                            <DocumentHeaderWithAvatar 
                                                avatars={avatars}
                                            />
                                        </Box> */}
                                        <Box
                                            sx={{
                                                padding: 0,
                                                height: '100%',
                                            }}
                                        >
                                            {activeTab === 0 && !isMemberRole &&
                                                !showSourceDocumentChunkPreview && (
                                                    mrtJson && (sections|| []).length > 0 ? (
                                                        <SidebarLayout>
                                                            <SectionTabComponent
                                                                id={id}
                                                                templateId={templateId}
                                                                onSourceCardClick={setSelectedInconsistencyPreview}
                                                                onOpenTemplateButtonClick={handleOpenTemplateButtonClick}
                                                                selectedSectionIdx={selectedSectionIdx}
                                                                setSelectedSectionIdx={setSelectedSectionIdx}
                                                                selectedText={selectedText}
                                                                setSelectedText={setSelectedText}
                                                                setError={setError}
                                                                setErrorMessage={setErrorMessage}
                                                                editorInstance={editorInstance}
                                                                mrtJson={mrtJson}
                                                                sections={sections}
                                                                setSections={setSections}
                                                                documentSources={documentSources}
                                                                setDocumentSources={setDocumentSources}
                                                                sourceDataReferences={sourceDataReferences}
                                                                setSourceDataReferences={setSourceDataReferences}
                                                                mrtTables={mrtTables}
                                                                setMRTTables={setMRTTables}
                                                                expanded={expanded}
                                                                handleAccordionChange={handleAccordionChange}
                                                                combinedDocumentSources={combinedDocumentSources}
                                                                setCombinedDocumentSources={setCombinedDocumentSources}
                                                                setIsRegenerating={setIsRegenerating}
                                                                handleOptionSelect={handleOptionSelect}
                                                                setSuccess={setSuccess}
                                                                setLoading={setLoading}
                                                                loading={loading}
                                                                sourceDocuments={sourceDocuments}
                                                                onFileSelect={handleFileSelect}
                                                                dateUploaded={dateUploaded}
                                                                templateNickname={templateNickname}
                                                                documentMRTTables={documentMRTTables}
                                                                connectorId={connectorId}
                                                                navigate={navigate}
                                                                documentSet={documentSet}
                                                                selectedSources={selectedSources}
                                                                selectedSectionSources={selectedSectionSources}
                                                                setSelectedSectionSources={setSelectedSectionSources}
                                                            />
                                                        </SidebarLayout>
                                                    ) : (
                                                        <Box sx={{ width: '100%' }}>
                                                            <ThemeProvider theme={ArtosTheme}>
                                                                <SidebarSkeletonLoader />
                                                            </ThemeProvider>
                                                        </Box>
                                                    )
                                                )}
                                            {activeTab === 1 && (
                                                <Box sx={{ color: "#94A3B8", fontSize: 15, textAlign: "center", mt: 8 }}>
                                                    No content yet.
                                                </Box>
                                            )}
                                        </Box>
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
            <GeneratingTemplateModal
                open={error}
                onClose={() => {
                    setError(false)
                    setErrorMessage('')
                    if (!isRegenerating) {
                        // refresh the page
                        window.location.reload(true)
                    }
                    setIsRegenerating(false)
                }}
                loading={false}
                success={false}
                error={true}
                errorMessage={errorMessage}
                buttonText="Close"
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>

            <GeneratingDocumentModal
                open={isRegenerating}
                onClose={handleRegenerateDocumentModalClose}
                loading={loading}
                success={success}
                error={error}
                loadingMessage="Regeneration in progress..."
                loadingSubMessage="This may take a moment."
                onSuccess={() => {
                    handleRegenerateDocumentModalClose()
                }}
            />

            <SourceSelectionModal
                open={sourceModalOpen}
                handleClose={() => setSourceModalOpen(false)}
                onSourceSelect={handleSourceSelect}
                documentName={documentName}
                id={documentId}
                setError={setError}
                orgId={orgs[0].orgId}
                org_name={orgs[0].orgName}
                selectedFileNames={selectedCloudFileNames}
                initialSelectedFiles={sourceDocuments.map((file) => ({
                    ...file
                }))}
                getOrgAccessToken={() => getOrgAccessToken(orgs[0].orgId)}
                // allowedFolders={['input', 'output', 'templates']}
                allowedFolders={['input']}
                allowedSelectedFileTypes={['pdf', 'docx', 'doc', 'txt', 'csv', 'xls', 'xlsx', 'ppt', 'pptx']}
            />
            
            {/* Hidden file input for uploading edited files */}
            <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileSelectInternal}
                accept=".doc,.docx,.pdf,.txt"
            />
        </Box>
    )
}

export default DocumentPage
